import { useEffect, useRef } from "react";
import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/shell/page";

export const Route = createFileRoute("/_app/$workspaceSlug/settings")({
  component: SettingsLayout,
});

const SECTIONS = [
  { id: "account", to: "/$workspaceSlug/settings/account" },
  { id: "workspace", to: "/$workspaceSlug/settings/workspace" },
  { id: "members", to: "/$workspaceSlug/settings/members" },
  { id: "projects", to: "/$workspaceSlug/settings/projects" },
  { id: "integrations", to: "/$workspaceSlug/settings/integrations" },
  { id: "agents", to: "/$workspaceSlug/settings/agents" },
] as const;

function SettingsLayout() {
  const { t } = useTranslation("settings");
  const params = Route.useParams();
  const nav = useRef<HTMLElement>(null);
  const pathname = useLocation({ select: (location) => location.pathname });
  // On a small screen the sections are a strip that scrolls: the one being
  // read has to be in it, or nothing says where you are.
  useEffect(() => {
    nav.current
      ?.querySelector('[data-status="active"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);
  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* A column beside the content from `md`, a scrolling strip above it
            on small screens. */}
        <nav
          ref={nav}
          aria-label={t("title")}
          className="flex shrink-0 gap-px overflow-x-auto border-b px-3 py-2 md:w-44 md:flex-col md:border-r md:border-b-0 md:px-2 md:py-3"
        >
          {SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.to}
              params={params}
              className="flex h-8 shrink-0 items-center rounded-md px-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground data-[status=active]:bg-accent data-[status=active]:font-medium data-[status=active]:text-foreground md:h-7"
            >
              {t(`sections.${section.id}`)}
            </Link>
          ))}
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-10 px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
}
